
import { Component, Vue } from 'vue-property-decorator';
import WebsiteRepositoryInterface from '@/services/Website/interfaces';
import { ServerInterface, ServerRepositoryInterface } from '@/services/Server/Interfaces';
import { WPXFormValue } from '@/components/templates/WPXForm';
import CreateWebsiteForm from '@/services/Website/forms/CreateWebsiteForm';
import WebsiteGroupRepositoryInterface, { WebsiteGroupItem } from '@/services/WebsiteGroup/interfaces';
import { UserInterface, UserRepoInterface } from '@/services/Users/Interfaces';
import { PlanInterface, PlanRepositoryInterface } from '@/services/Plan/interfaces';
import PageHeader from '@/components/layout/PageHeader.vue';

@Component({
  components: {
    PageHeader,
  },
  inject: [
    'websiteRepository',
    'serverRepository',
    'websiteGroupRepository',
    'userRepository',
    'planRepository',
  ],
})
export default class CreateWebsite extends Vue {
  [x: string]: any;

  public loading: boolean = true;

  public websiteRepository!: WebsiteRepositoryInterface;
  public serverRepository!: ServerRepositoryInterface;
  public websiteGroupRepository!: WebsiteGroupRepositoryInterface;
  public userRepository!: UserRepoInterface;
  public planRepository!: PlanRepositoryInterface;

  public form: WPXFormValue<CreateWebsiteForm> = new WPXFormValue<CreateWebsiteForm>(CreateWebsiteForm);

  public servers: ServerInterface[] = [];
  public groups: WebsiteGroupItem[] = [];
  public users: UserInterface[] = [];
  public plans: PlanInterface[] = [];
  public planLocked: boolean = false;
  public groupsLoading: boolean = false;
  public autoFilling: boolean = false;
  public enableEmail: boolean = false;
  public enableWordpress: boolean = false;
  public fromGroupId: number | null = null;

  public get excludeActions(): string[] {
    const excluded: string[] = [];
    if (!this.enableEmail) {
      excluded.push('email');
    }
    if (!this.enableWordpress) {
      excluded.push('wp');
    }
    return excluded;
  }

  public get cancelRoute(): { name: string; params?: { id: string } } {
    if (this.fromGroupId) {
      return { name: 'website_group_view', params: { id: String(this.fromGroupId) } };
    }

    return { name: 'website_all' };
  }

  public get cancelLabel(): string {
    return this.fromGroupId ? 'Back to group' : 'Back to websites';
  }

  public async created(): Promise<void> {
    try {
      await this.initialize();
      this.loading = false;
    } catch (err) {
      this.$errorDialog.show(err.response?.data?.message || err.message);
    }
  }

  public async initialize(): Promise<void> {
    await this.getServers();
    await this.getUsers();
    await this.getPlans();
    await this.applyQueryPrefill();
  }

  public async getServers(): Promise<void> {
    this.servers = await this.serverRepository.getAllServers();
  }

  public serverLabel(server: ServerInterface): string {
    return `${server.hostname} (${server.status})`;
  }

  public userRole(user: UserInterface): string {
    const role = user.role as unknown;
    if (!role) {
      return '';
    }

    if (typeof role === 'string') {
      return role;
    }

    return (role as { name?: string }).name || '';
  }

  public userLabel(user: UserInterface): string {
    const role = this.userRole(user);
    return role ? `${user.username} (${role})` : user.username;
  }

  public planLabel(plan: PlanInterface): string {
    const bandwidth = plan.bandwidth > 0 ? this.formatKiB(plan.bandwidth) : 'Unlimited';
    return `${plan.name} — ${this.formatKiB(plan.quota)} / ${bandwidth} / ${plan.websitesCount} site${plan.websitesCount === 1 ? '' : 's'}`;
  }

  public get selectedGroup(): WebsiteGroupItem | null {
    if (!this.form.data.groupId) {
      return null;
    }

    return this.groups.find((g) => g.id === this.form.data.groupId) || null;
  }

  public get groupPlaceholder(): string {
    if (!this.form.data.originServer) {
      return 'Select a server first';
    }

    if (this.groupsLoading) {
      return 'Loading groups…';
    }

    if (!this.groups.length) {
      return 'No groups found';
    }

    return 'Search by domain or group id';
  }

  public get groupEmptyText(): string {
    if (!this.form.data.originServer) {
      return 'Select a server to load groups.';
    }

    if (this.groupsLoading) {
      return 'Loading groups…';
    }

    return 'No groups found for this server/owner.';
  }

  public groupPlanName(group: WebsiteGroupItem): string {
    const plan = this.plans.find((p) => p.id === group.plan);
    return plan ? plan.name : (group.plan ? `Plan #${group.plan}` : 'No plan');
  }

  public groupLabel(group: WebsiteGroupItem): string {
    if (group.mainDomain) {
      return `${group.mainDomain} (#${group.id})`;
    }

    return `Group #${group.id}`;
  }

  public groupSubtitle(group: WebsiteGroupItem): string {
    const parts = [
      this.groupPlanName(group),
      `${group.websiteCount} site${group.websiteCount === 1 ? '' : 's'}`,
    ];

    if (group.owner) {
      parts.push(group.owner);
    }

    if (group.mainWebsite) {
      parts.push(`main #${group.mainWebsite}`);
    }

    return parts.join(' · ');
  }

  public async loadServerGroups(): Promise<void> {
    if (!this.form.data.originServer) {
      return;
    }

    if (!this.fromGroupId) {
      this.form.data.userId = '';
      this.form.data.groupId = null;
      this.planLocked = false;
    }

    this.groups = [];
    this.groupsLoading = true;

    try {
      if (this.fromGroupId && this.form.data.userId) {
        this.groups = await this.websiteGroupRepository.getServerGroups(
          this.form.data.originServer,
          this.form.data.userId,
        );
      } else {
        this.groups = await this.websiteGroupRepository.getServerGroups(this.form.data.originServer);
      }

      if (this.fromGroupId) {
        await this.ensureGroupInList(this.fromGroupId);
        this.form.data.groupId = this.fromGroupId;
        await this.setUserId(this.fromGroupId);
      }
    } catch (err) {
      this.$errorDialog.show(err.response?.data?.error || err.message);
      this.groups = [];
    } finally {
      this.groupsLoading = false;
    }
  }

  public async applyQueryPrefill(): Promise<void> {
    const query = this.$route.query;
    const groupIdRaw = Array.isArray(query.groupId) ? query.groupId[0] : query.groupId;
    const originServerRaw = Array.isArray(query.originServer) ? query.originServer[0] : query.originServer;
    const userIdRaw = Array.isArray(query.userId) ? query.userId[0] : query.userId;
    const planIdRaw = Array.isArray(query.planId) ? query.planId[0] : query.planId;

    const groupId = groupIdRaw ? Number(groupIdRaw) : NaN;
    const originServer = typeof originServerRaw === 'string' ? originServerRaw : '';
    const userId = typeof userIdRaw === 'string' ? userIdRaw : '';
    const planId = planIdRaw ? Number(planIdRaw) : NaN;

    if (!groupIdRaw && !originServer) {
      return;
    }

    if (!Number.isNaN(groupId)) {
      this.fromGroupId = groupId;
    }

    if (originServer) {
      this.form.data.originServer = originServer;
    }

    if (!this.form.data.originServer && !Number.isNaN(groupId)) {
      try {
        const full = await this.websiteGroupRepository.getGroup(groupId);
        const main = (full.websites || []).find((website) => website.id === full.mainWebsite)
          || (full.websites || [])[0];
        if (main && main.originServer) {
          this.form.data.originServer = main.originServer;
        }
        this.groups = [{
          id: full.id,
          owner: full.owner,
          mainWebsite: full.mainWebsite,
          websiteCount: (full.websites || []).length,
          mainDomain: main ? main.domain : undefined,
          plan: full.plan,
        }];
      } catch (err) {
        // Fall through; group selection may still fail without a server.
      }
    }

    if (!this.form.data.originServer) {
      return;
    }

    this.groupsLoading = true;
    try {
      if (userId) {
        this.groups = await this.websiteGroupRepository.getServerGroups(
          this.form.data.originServer,
          userId,
        );
      } else {
        this.groups = await this.websiteGroupRepository.getServerGroups(this.form.data.originServer);
      }
    } catch (err) {
      this.$errorDialog.show(err.response?.data?.error || err.message);
      this.groups = [];
    } finally {
      this.groupsLoading = false;
    }

    if (!Number.isNaN(groupId)) {
      await this.ensureGroupInList(groupId, userId, planId);
      this.form.data.groupId = groupId;
      await this.setUserId(groupId);
      return;
    }

    if (userId) {
      this.form.data.userId = userId;
    }

    if (!Number.isNaN(planId)) {
      this.form.data.planId = planId;
    }
  }

  public async ensureGroupInList(
    groupId: number,
    fallbackOwner: string = '',
    fallbackPlan: number = NaN,
  ): Promise<void> {
    if (this.groups.some((group) => group.id === groupId)) {
      return;
    }

    try {
      const full = await this.websiteGroupRepository.getGroup(groupId);
      const main = (full.websites || []).find((website) => website.id === full.mainWebsite);
      this.groups = [{
        id: full.id,
        owner: full.owner,
        mainWebsite: full.mainWebsite,
        websiteCount: (full.websites || []).length,
        mainDomain: main ? main.domain : undefined,
        plan: full.plan,
      }, ...this.groups];
    } catch (err) {
      this.groups = [{
        id: groupId,
        owner: fallbackOwner || this.form.data.userId || '',
        mainWebsite: 0,
        websiteCount: 0,
        plan: !Number.isNaN(fallbackPlan) ? fallbackPlan : undefined,
      }, ...this.groups];
    }
  }

  public async onOwnerChange(userId: string | null): Promise<void> {
    this.form.data.groupId = null;
    this.planLocked = false;

    if (!userId) {
      await this.reloadGroupsForServer();
      return;
    }

    await this.loadUserGroups(userId);
  }

  public async reloadGroupsForServer(): Promise<void> {
    if (!this.form.data.originServer) {
      this.groups = [];
      return;
    }

    this.groupsLoading = true;
    try {
      this.groups = await this.websiteGroupRepository.getServerGroups(this.form.data.originServer);
    } catch (err) {
      this.$errorDialog.show(err.response?.data?.error || err.message);
      this.groups = [];
    } finally {
      this.groupsLoading = false;
    }
  }

  public async loadUserGroups(userId: string): Promise<void> {
    if (!userId) {
      return;
    }

    if (!this.form.data.originServer) {
      this.$errorDialog.show('Select a server before filtering groups by owner.');
      return;
    }

    this.groups = [];
    this.groupsLoading = true;

    try {
      this.groups = await this.websiteGroupRepository.getServerGroups(
        this.form.data.originServer,
        userId,
      );
    } catch (err) {
      this.$errorDialog.show(err.response?.data?.error || err.message);
      this.groups = [];
    } finally {
      this.groupsLoading = false;
    }
  }

  public async getUsers(): Promise<void> {
    [this.users] = await this.userRepository.getAllUsers();
  }

  public async getPlans(): Promise<void> {
    this.plans = await this.planRepository.getAll();
  }

  public async setUserId(groupId: number): Promise<void> {
    if (!groupId) {
      this.planLocked = false;
      this.form.data.planId = null;
      return;
    }

    const group = this.groups.find((g) => g.id === groupId);
    if (!group || !group.owner) {
      this.planLocked = false;
      this.form.data.planId = null;
      return;
    }

    this.users = this.users.filter((u) => u.id !== u.username);

    this.form.data.userId = group.owner;

    this.users.push({ id: group.owner, username: group.owner } as unknown as UserInterface);

    this.form.data.planId = group.plan ?? 1;

    this.planLocked = true;
  }

  public async createWebsite(): Promise<void> {
    try {
      await this.websiteRepository.createWebsite(
        this.form.data.originServer,
        this.form.data.domain,
        this.form?.data?.userId ? this.form.data.userId : this.$store.getters.id,
        this.form.data.groupId,
        this.excludeActions,
        this.form?.data?.planId || 1,
      );

      this.$successDialog.show('Website creation started!');
      await this.$router.push(this.cancelRoute);
    } catch (err) {
      this.$errorDialog.show(err.response?.data?.error || err.message);
    }
  }

  public async autoFill(): Promise<void> {
    this.autoFilling = true;

    try {
      const upServer = this.servers.find((server) => server.status === 'Up');
      if (!upServer) {
        this.$errorDialog.show('No Up server available to auto-fill.');
        return;
      }

      this.form.data.originServer = upServer._id;
      this.form.data.domain = this.generateRandomDomain();
      this.form.data.groupId = null;
      this.planLocked = false;

      const currentUserId = this.$store.getters.id as string;
      const owner = this.users.find((user) => user.id === currentUserId) || this.users[0];
      this.form.data.userId = owner ? owner.id : currentUserId || '';

      const defaultPlan = this.plans.find((plan) => plan.name === 'Default') || this.plans[0];
      this.form.data.planId = defaultPlan ? defaultPlan.id : 1;

      this.enableEmail = true;
      this.enableWordpress = true;

      this.groupsLoading = true;
      try {
        if (this.form.data.userId) {
          this.groups = await this.websiteGroupRepository.getServerGroups(
            this.form.data.originServer,
            this.form.data.userId,
          );
        } else {
          this.groups = await this.websiteGroupRepository.getServerGroups(this.form.data.originServer);
        }
      } catch (err) {
        this.groups = [];
      } finally {
        this.groupsLoading = false;
      }
    } finally {
      this.autoFilling = false;
    }
  }

  private generateRandomDomain(): string {
    const adjectives = ['bright', 'calm', 'swift', 'lunar', 'coral', 'amber', 'nova', 'quiet'];
    const nouns = ['harbor', 'summit', 'grove', 'signal', 'orbit', 'cedar', 'pixel', 'ridge'];
    const adj = adjectives[Math.floor(Math.random() * adjectives.length)];
    const noun = nouns[Math.floor(Math.random() * nouns.length)];
    const suffix = Math.random().toString(36).replace(/[^a-z0-9]/g, '').slice(0, 6);

    return `${adj}-${noun}-${suffix}.example.test`;
  }
}
